<template>
  <div>
    <div class="contact-form">
      <div class="sp-3 mt-3">
        <h4>Tipos de Processo e Time Operacional</h4>
      </div>

      <div
        v-for="typeOfProcess in localTypesOfProcess"
        :key="typeOfProcess.customerDetailsKey"
      >
        <v-switch
          name="hasCompliance"
          :labels="{
            checked: `${typeOfProcess.label}`,
            unchecked: `${typeOfProcess.label}`,
          }"
          :value="customerDetails[typeOfProcess.customerDetailsKey]"
          @input="(e) => setTypeOfProcess(e, typeOfProcess.customerDetailsKey)"
          class="mb-2"
        />
      </div>

      <div v-for="(typeOfProcess, index) in localTypesOfProcess" :key="index">
        <select-wrapper
          :value="customerDetails[typeOfProcess.teamDetailsKey]"
          @input="(v) => onOperationalTeamChange(v, typeOfProcess.teamDetailsKey)"
          :options="teams"
          :label="typeOfProcess.teamLabel"
          v-if="customerDetails[typeOfProcess.customerDetailsKey]"
        ></select-wrapper>
      </div>
    </div>
    <div class="contact-form">
      <div class="sp-3 mt-3 bd-t pd-t-10">
        <h4>Time Aduaneiro</h4>
      </div>
      <div>
        <select-wrapper
          v-if="customsTeamSelectReady"
          v-model="customerDetails.customsTeam"
          label="Time Aduaneiro"
          :options="customsTeams"
        ></select-wrapper>
        <p v-else class="text-muted small mb-0">Carregando times aduaneiros...</p>
      </div>
    </div>
  </div>
</template>

<script>
import VSwitch from "@/components/VSwitch.vue";
import SelectWrapper from "@/components/SelectWrapper.vue";
import Swal from "sweetalert2";
import Axios from "axios";
import config from "@/config.js";

export default {
  name: "CustomerTeamsForm",
  props: {
    customerDetails: {
      type: Object,
      required: true,
    },
    showLoading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VSwitch,
    SelectWrapper,
  },
  data() {
    return {
      localTypesOfProcess: [
        {
          label: "Conta e Ordem",
          teamLabel: "Time para Conta e Ordem",
          customerDetailsKey: "hasTypeAccountAndOrder",
          teamDetailsKey: "team",
        },
        {
          label: "Encomenda",
          teamLabel: "Time para Encomenda",
          customerDetailsKey: "hasTypeOrder",
          teamDetailsKey: "teamOrder",
        },
        {
          label: "Assessoria",
          teamLabel: "Time para Assessoria",
          customerDetailsKey: "hasTypeAdvisory",
          teamDetailsKey: "teamAdvisory",
        },
      ],
      teams: [],
      customsTeams: [],
      customsTeamsLoaded: false,
    };
  },
  computed: {
    customsTeamSelectReady() {
      if (this.customsTeamsLoaded) {
        return true;
      }
      const id = this.normalizeTeamId(this.customerDetails.customsTeam);
      if (id == null) {
        return false;
      }
      const option = this.customsTeams.find(
        (o) => String(o.value) === String(id)
      );
      return !!(
        option &&
        option.label &&
        option.label !== String(id)
      );
    },
  },
  created() {
    this.normalizeTeamFields();
  },
  async mounted() {
    this.normalizeTeamFields();
    await this.loadOptions();
  },
  watch: {
    customerDetails: {
      deep: true,
      immediate: true,
      handler() {
        this.normalizeTeamFields();
      },
    },
    "customerDetails.id": {
      async handler() {
        this.customsTeams = [];
        this.customsTeamsLoaded = false;
        this.normalizeTeamFields();
        await this.loadOptions();
      },
    },
  },
  methods: {
    mergeCustomsTeamOptions(existing, incoming) {
      const byValue = {};
      existing.forEach((option) => {
        byValue[String(option.value)] = option;
      });
      incoming.forEach((option) => {
        const key = String(option.value);
        const current = byValue[key];
        const hasLabel =
          option.label &&
          String(option.label).trim() &&
          option.label !== String(option.value);
        if (!current || hasLabel) {
          byValue[key] = {
            value: option.value,
            label: hasLabel ? option.label : (current && current.label) || String(option.value),
          };
        }
      });
      return Object.values(byValue);
    },
    seedCustomsTeamOption() {
      const value = this.customerDetails.customsTeam;
      if (value == null || value === "") {
        return;
      }

      let id;
      let label;

      if (typeof value === "object" && value.id != null) {
        id = value.id;
        label = value.name || value.label;
      } else {
        id = value;
        label = this.customerDetails.customsTeamName;
      }

      if (id == null || id === "") {
        return;
      }

      const trimmedLabel = label && String(label).trim();
      if (!trimmedLabel) {
        return;
      }

      this.customsTeams = this.mergeCustomsTeamOptions(this.customsTeams, [
        { value: id, label: trimmedLabel },
      ]);
    },
    normalizeTeamFields() {
      this.seedCustomsTeamOption();
      const fields = ["team", "teamOrder", "teamAdvisory", "customsTeam"];
      fields.forEach((field) => {
        const value = this.customerDetails[field];
        if (value && typeof value === "object" && value.id != null) {
          this.customerDetails[field] = value.id;
        }
      });
    },
    async loadOptions() {
		this.$store.commit("setLoading", {
			show: true,
			label: "Carregando...",
			description: "",
		});
		if (!this.customsTeamsLoaded) {
			this.seedCustomsTeamOption();
		}
		await Promise.all([this.getTeams(), this.getCustomsTeams()]);
		if (this.customerDetails.id) {
			await this.getCustomerTeams();
		}
		this.$store.commit("setLoading", {
			show: false,
		});
    },
    setTypeOfProcess(value, key) {
      this.customerDetails[key] = value;

      if (!value) {
        Swal.fire({
          title: "Tipos de Processo",
          html: "<p>Desativando este Tipo de Processo, você removerá o vínculo com o Time / Equipe Operacional que estava selecionada.</p>",
          type: "warning",
        });
      }
    },
    normalizeTeamId(value) {
      if (value === "" || value === undefined) {
        return null;
      }
      return value;
    },
    teamIdsEqual(a, b) {
      return String(this.normalizeTeamId(a) ?? "") === String(this.normalizeTeamId(b) ?? "");
    },
    onOperationalTeamChange(newValue, teamDetailsKey) {
      const previous = this.customerDetails[teamDetailsKey];
      if (this.teamIdsEqual(previous, newValue)) {
        return;
      }
      const hadPrevious = this.normalizeTeamId(previous) != null;
      if (!hadPrevious) {
        this.customerDetails[teamDetailsKey] = newValue;
        return;
      }
      Swal.fire({
        title: "Alteração de time operacional",
        html:
          "<p>Os processos já existentes deste cliente continuam vinculados ao time que estava selecionado.</p><p>Para alterar o time de processos em aberto, faça a troca diretamente em cada processo.</p>",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        confirmButtonText: "Continuar",
      }).then((result) => {
        if (result.value) {
          this.customerDetails[teamDetailsKey] = newValue;
        }
      });
    },
    validateProcessTeams() {
      if (
        this.customerDetails.hasTypeAccountAndOrder &&
        this.customerDetails.team == null
      ) {
        return true;
      }

      if (this.customerDetails.hasTypeOrder && this.customerDetails.teamOrder == null) {
        return true;
      }

      if (
        this.customerDetails.hasTypeAdvisory &&
        this.customerDetails.teamAdvisory == null
      ) {
        return true;
      }

      return false;
    },
    buildPayload() {
      return {
        hasTypeAccountAndOrder: !!this.customerDetails.hasTypeAccountAndOrder,
        hasTypeOrder: !!this.customerDetails.hasTypeOrder,
        hasTypeAdvisory: !!this.customerDetails.hasTypeAdvisory,
        team: this.normalizeTeamId(this.customerDetails.team),
        teamOrder: this.normalizeTeamId(this.customerDetails.teamOrder),
        teamAdvisory: this.normalizeTeamId(this.customerDetails.teamAdvisory),
        customsTeam: this.normalizeTeamId(this.customerDetails.customsTeam),
      };
    },
    async getCustomsTeams() {
      try {
        const response = await Axios.get(
          `${config.env.API_DEFAULT.host}/customs-teams`
        );
        const data = response.data && response.data.data;
        const list = Array.isArray(data) ? data : [];
        const fromApi = list.map((ct) => ({
          value: ct.id,
          label: ct.name,
        }));
        this.customsTeams = this.mergeCustomsTeamOptions(this.customsTeams, fromApi);
        this.customsTeamsLoaded = true;
      } catch (error) {
        Swal.fire({
          title: "Times Aduaneiros",
          html: `<p>Não foi possível carregar os times aduaneiros.</p><div>${error}</div>`,
          type: "error",
        });
      }
    },
    async getTeams() {
      return Axios.get(`${config.env.API_DEFAULT.host}/teams`)
        .then((response) => {
          this.teams = response.data._embedded.teams.map((team) => ({
            label: `${team.name}`,
            value: team.id,
          }));
        })
        .catch((error) => {
          Swal.fire({
            title: "Listar Times!",
            html: `<p>Não foi possível carregar os times.</p><div>${error}</div>`,
            type: "error",
          });
        });
    },
    async getCustomerTeams() {
      if (!this.customerDetails.id) {
        return;
      }

      if (this.showLoading) {
        this.$store.commit("setLoading", {
          show: true,
          label: "Carregando Times do Cliente",
          description: "Aguarde...",
        });
      }

      try {
        await Promise.all([
          Axios.get(
            `${config.env.API_DEFAULT.host}/customers/getCustomerTeam/${this.customerDetails.id}`
          ).then((response) => {
            this.customerDetails.team = response.data.data
              ? response.data.data.id
              : null;
          }),
          Axios.get(
            `${config.env.API_DEFAULT.host}/customers/getCustomerTeamOrder/${this.customerDetails.id}`
          ).then((response) => {
            this.customerDetails.teamOrder = response.data.data
              ? response.data.data.id
              : null;
          }),
          Axios.get(
            `${config.env.API_DEFAULT.host}/customers/getCustomerTeamAdvisory/${this.customerDetails.id}`
          ).then((response) => {
            this.customerDetails.teamAdvisory = response.data.data
              ? response.data.data.id
              : null;
          }),
        ]);
        this.normalizeTeamFields();
      } catch (e) {
        Swal.fire({
          title: "Listar Times Vinculados!",
          html: `<p>Não foi possível carregar os times.</p><div>${e}</div>`,
          type: "error",
        });
      } finally {
        if (this.showLoading) {
          this.$store.commit("setLoading", { show: false });
        }
      }
    },
  },
};
</script>
